import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchSearch, type SearchResponse } from "../../lib/catalog";
import { createSalesCustomer, fetchSalesCustomers, type SalesCustomer } from "../../lib/sales-customers";
import {
  createSalesOrder,
  fetchSalesOrder,
  fetchSalesOrders,
  updateSalesOrder,
  type SalesOrderDetail,
  type SalesOrderSummary
} from "../../lib/sales-orders";
import { formatCurrency, formatDateTime } from "../../lib/utils";

type OrderStatus = "Pending" | "Paid" | "Processing" | "Delivered" | "Cancelled";
type PaymentType = "" | "MPESA" | "CASH" | "CHEQUE" | "BANK_TRANSFER";
type PanelMode = "detail" | "create";
type CustomerMode = "existing" | "new";

type EditorState = {
  id: number | null;
  status: OrderStatus;
  trackingNumber: string;
  amountPaid: string;
  paymentType: PaymentType;
};

type DraftItem = {
  productId: number;
  name: string;
  slug: string;
  price: number;
  quantity: string;
  stock: number;
};

type CreateOrderState = {
  customerMode: CustomerMode;
  customerId: number | null;
  deliveryName: string;
  deliveryPhone: string;
  deliveryAddress: string;
  deliveryNote: string;
  trackingNumber: string;
  amountPaid: string;
  paymentType: PaymentType;
  status: OrderStatus;
  newCustomerName: string;
  newCustomerEmail: string;
  newCustomerPhone: string;
  newCustomerAddress: string;
  items: DraftItem[];
};

const emptyEditorState: EditorState = {
  id: null,
  status: "Pending",
  trackingNumber: "",
  amountPaid: "0",
  paymentType: ""
};

const emptyCreateOrderState: CreateOrderState = {
  customerMode: "existing",
  customerId: null,
  deliveryName: "",
  deliveryPhone: "",
  deliveryAddress: "",
  deliveryNote: "",
  trackingNumber: "",
  amountPaid: "0",
  paymentType: "",
  status: "Pending",
  newCustomerName: "",
  newCustomerEmail: "",
  newCustomerPhone: "",
  newCustomerAddress: "",
  items: []
};

const orderStatuses: OrderStatus[] = ["Pending", "Paid", "Processing", "Delivered", "Cancelled"];
const paymentTypes: Exclude<PaymentType, "">[] = ["MPESA", "CASH", "CHEQUE", "BANK_TRANSFER"];

function toEditorState(order: SalesOrderDetail): EditorState {
  return {
    id: order.id,
    status: (order.status as OrderStatus) || "Pending",
    trackingNumber: order.trackingNumber,
    amountPaid: String(order.amountPaid),
    paymentType: (order.paymentType as PaymentType) || ""
  };
}

function statusTone(status: string) {
  switch (status) {
    case "Pending":
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300";
    case "Delivered":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
    case "Paid":
      return "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-900/70 dark:bg-cyan-950/40 dark:text-cyan-300";
    case "Processing":
      return "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/40 dark:text-violet-300";
    case "Cancelled":
      return "border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300";
    default:
      return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300";
  }
}

function paymentProgressTone(order: Pick<SalesOrderSummary, "balance" | "amountPaid" | "total">) {
  if (order.total <= 0) {
    return "text-nv-slate";
  }
  if (order.balance <= 0) {
    return "text-emerald-600 dark:text-emerald-300";
  }
  if (order.amountPaid > 0) {
    return "text-amber-600 dark:text-amber-300";
  }
  return "text-red-600 dark:text-red-300";
}

function paymentTypeLabel(value: PaymentType | string) {
  switch (value) {
    case "MPESA":
      return "M-Pesa";
    case "CASH":
      return "Cash";
    case "CHEQUE":
      return "Cheque";
    case "BANK_TRANSFER":
      return "Bank Transfer";
    default:
      return value || "Not recorded";
  }
}

function selectedCustomerLabel(customer: SalesCustomer | null) {
  return customer ? customer.name : "No customer selected";
}

export function SalesOrdersPage() {
  const [panelMode, setPanelMode] = useState<PanelMode>("detail");
  const [orders, setOrders] = useState<SalesOrderSummary[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<SalesOrderDetail | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | OrderStatus>("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalOrders: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [summary, setSummary] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    paidOrders: 0,
    revenueTotal: 0,
    thisMonthRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [createOrder, setCreateOrder] = useState<CreateOrderState>(emptyCreateOrderState);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState<SalesCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<SalesCustomer | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState<SearchResponse["results"]>([]);
  const [productLoading, setProductLoading] = useState(false);

  const metrics = useMemo(
    () => [
      { label: "Total orders", value: summary.totalOrders, compact: false },
      { label: "Pending follow-up", value: summary.pendingOrders, compact: false },
      { label: "Paid in full", value: summary.paidOrders, compact: false },
      { label: "This month revenue", value: formatCurrency(summary.thisMonthRevenue), compact: true }
    ],
    [summary]
  );

  const draftTotals = useMemo(() => {
    const subtotal = createOrder.items.reduce((sum, item) => sum + item.price * Math.max(Number(item.quantity) || 0, 0), 0);
    const amountPaid = Math.max(Number(createOrder.amountPaid) || 0, 0);
    return {
      subtotal,
      amountPaid,
      balance: Math.max(subtotal - amountPaid, 0)
    };
  }, [createOrder.amountPaid, createOrder.items]);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("status", statusFilter);
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchSalesOrders(params);
        if (!ignore) {
          setOrders(result.orders);
          setPagination(result.pagination);
          setSummary(result.summary);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load sales orders.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [page, query, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter]);

  useEffect(() => {
    let ignore = false;

    async function loadCustomers() {
      if (panelMode !== "create" || createOrder.customerMode !== "existing" || customerQuery.trim().length < 2) {
        if (!ignore) {
          setCustomerResults([]);
        }
        return;
      }

      if (selectedCustomer && customerQuery.trim() === selectedCustomer.name.trim()) {
        if (!ignore) {
          setCustomerResults([]);
        }
        return;
      }

      setCustomerLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("q", customerQuery.trim());
        const result = await fetchSalesCustomers(params);
        if (!ignore) {
          setCustomerResults(result.customers);
        }
      } catch {
        if (!ignore) {
          setCustomerResults([]);
        }
      } finally {
        if (!ignore) {
          setCustomerLoading(false);
        }
      }
    }

    void loadCustomers();
    return () => {
      ignore = true;
    };
  }, [customerQuery, createOrder.customerMode, panelMode, selectedCustomer]);

  useEffect(() => {
    let ignore = false;

    async function loadProducts() {
      if (panelMode !== "create" || productQuery.trim().length < 2) {
        if (!ignore) {
          setProductResults([]);
        }
        return;
      }

      setProductLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("q", productQuery.trim());
        const result = await fetchSearch(params);
        if (!ignore) {
          setProductResults(result.results.slice(0, 10));
        }
      } catch {
        if (!ignore) {
          setProductResults([]);
        }
      } finally {
        if (!ignore) {
          setProductLoading(false);
        }
      }
    }

    void loadProducts();
    return () => {
      ignore = true;
    };
  }, [panelMode, productQuery]);

  async function reloadCurrentView() {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set("q", query.trim());
    }
    params.set("status", statusFilter);
    params.set("page", String(page));
    params.set("pageSize", "25");
    const result = await fetchSalesOrders(params);
    setOrders(result.orders);
    setPagination(result.pagination);
    setSummary(result.summary);
  }

  function openCreateOrder() {
    setPanelMode("create");
    setEditorOpen(true);
    setSelectedOrder(null);
    setEditor(emptyEditorState);
    setCreateOrder(emptyCreateOrderState);
    setSelectedCustomer(null);
    setCustomerQuery("");
    setCustomerResults([]);
    setProductQuery("");
    setProductResults([]);
    setError(null);
    setSuccessMessage(null);
  }

  async function openOrder(order: SalesOrderSummary) {
    setPanelMode("detail");
    setEditorOpen(true);
    setLoadingEditor(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const detail = await fetchSalesOrder(order.id);
      setSelectedOrder(detail);
      setEditor(toEditorState(detail));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load order details.");
    } finally {
      setLoadingEditor(false);
    }
  }

  function applyCustomer(customer: SalesCustomer) {
    setSelectedCustomer(customer);
    setCustomerResults([]);
    setCustomerQuery(customer.name);
    setCreateOrder((current) => ({
      ...current,
      customerId: customer.id,
      deliveryName: current.deliveryName || customer.name,
      deliveryPhone: current.deliveryPhone || customer.phone,
      deliveryAddress: current.deliveryAddress || customer.address
    }));
  }

  function addDraftItem(product: SearchResponse["results"][number]) {
    setCreateOrder((current) => {
      const existing = current.items.find((item) => item.productId === product.id);
      if (existing) {
        return {
          ...current,
          items: current.items.map((item) =>
            item.productId === product.id
              ? { ...item, quantity: String((Number(item.quantity) || 1) + 1) }
              : item
          )
        };
      }

      return {
        ...current,
        items: [
          ...current.items,
          {
            productId: product.id,
            name: product.name,
            slug: product.slug,
            price: product.price,
            quantity: "1",
            stock: product.quantity
          }
        ]
      };
    });
    setProductQuery("");
    setProductResults([]);
  }

  async function handleCreateCustomerInline() {
    if (!createOrder.newCustomerName.trim()) {
      setError("Customer name is required.");
      return;
    }

    try {
      setError(null);
      const result = await createSalesCustomer({
        name: createOrder.newCustomerName,
        email: createOrder.newCustomerEmail,
        phone: createOrder.newCustomerPhone,
        address: createOrder.newCustomerAddress
      });

      setSelectedCustomer(result.customer);
      setCreateOrder((current) => ({
        ...current,
        customerMode: "existing",
        customerId: result.customer.id,
        deliveryName: current.deliveryName || result.customer.name,
        deliveryPhone: current.deliveryPhone || result.customer.phone,
        deliveryAddress: current.deliveryAddress || result.customer.address
      }));
      setSuccessMessage("Customer created and selected.");
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Unable to create customer.");
    }
  }

  async function handleCreateOrder(event: FormEvent) {
    event.preventDefault();

    if (createOrder.items.length === 0) {
      setError("Add at least one product.");
      return;
    }
    if (createOrder.customerMode === "existing" && !createOrder.customerId) {
      setError("Select an existing customer or switch to new customer.");
      return;
    }
    if (createOrder.customerMode === "new" && !createOrder.newCustomerName.trim()) {
      setError("Enter the new customer name.");
      return;
    }
    if (!createOrder.deliveryName.trim() || !createOrder.deliveryPhone.trim() || !createOrder.deliveryAddress.trim()) {
      setError("Delivery name, phone, and address are required.");
      return;
    }

    setCreatingOrder(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const created = await createSalesOrder({
        customerId: createOrder.customerMode === "existing" ? createOrder.customerId : null,
        customer:
          createOrder.customerMode === "new"
            ? {
                name: createOrder.newCustomerName,
                email: createOrder.newCustomerEmail,
                phone: createOrder.newCustomerPhone,
                address: createOrder.newCustomerAddress
              }
            : null,
        deliveryName: createOrder.deliveryName,
        deliveryPhone: createOrder.deliveryPhone,
        deliveryAddress: createOrder.deliveryAddress,
        deliveryNote: createOrder.deliveryNote,
        trackingNumber: createOrder.trackingNumber,
        amountPaid: Math.max(Number(createOrder.amountPaid) || 0, 0),
        paymentType: createOrder.paymentType,
        status: createOrder.status,
        items: createOrder.items.map((item) => ({
          productId: item.productId,
          quantity: Math.max(Number(item.quantity) || 1, 1)
        }))
      });

      await reloadCurrentView();
      const detail = await fetchSalesOrder(created.orderId);
      setPanelMode("detail");
      setSelectedOrder(detail);
      setEditor(toEditorState(detail));
      setCreateOrder(emptyCreateOrderState);
      setSelectedCustomer(null);
      setSuccessMessage(`Walk-in order #${created.orderId} created successfully.`);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Unable to create order.");
    } finally {
      setCreatingOrder(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!editor.id) {
      return;
    }

    const amountPaid = Number(editor.amountPaid);
    if (Number.isNaN(amountPaid) || amountPaid < 0) {
      setError("Amount paid must be a valid non-negative number.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateSalesOrder(editor.id, {
        status: editor.status,
        trackingNumber: editor.trackingNumber,
        amountPaid,
        paymentType: editor.paymentType
      });

      await reloadCurrentView();
      const refreshed = await fetchSalesOrder(editor.id);
      setSelectedOrder(refreshed);
      setEditor(toEditorState(refreshed));
      setSuccessMessage("Order changes saved successfully.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save order changes.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sales orders</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-nv-ink">Order queue</h1>
          <p className="mt-4 max-w-3xl text-nv-slate">
            Work the live order queue, confirm payments, and create walk-in orders from the sales desk without stepping into admin-only operations.
          </p>
        </div>
        <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white" onClick={openCreateOrder} type="button">
          New walk-in order
        </button>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(360px,1fr)]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {metrics.map((metric) => (
              <div className="admin-card rounded-[28px] p-5" key={metric.label}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{metric.label}</p>
                <p className={`mt-3 min-w-0 overflow-hidden text-nv-ink ${metric.compact ? "text-2xl leading-tight break-words" : "text-3xl font-semibold tracking-tight"}`}>
                  {metric.value}
                </p>
              </div>
            ))}
          </div>

          <div className="admin-card rounded-[28px] p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
              <input
                className="field-control px-4 py-3 text-sm"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by order, customer, phone, address, or tracking number"
                value={query}
              />
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)} value={statusFilter}>
                <option value="all">All statuses</option>
                {orderStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}
          {successMessage ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">{successMessage}</div> : null}

          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">Order</th>
                    <th className="px-5 py-4">Customer</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Payment</th>
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Tracking</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>Loading orders...</td></tr>
                  ) : orders.length > 0 ? (
                    orders.map((order) => (
                      <tr className="cursor-pointer border-b border-black/5 align-top transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.03]" key={order.id} onClick={() => void openOrder(order)}>
                        <td className="px-5 py-4">
                          <p className="font-semibold text-nv-ink">#{order.id}</p>
                          <p className="mt-1 text-xs text-nv-slate">{formatDateTime(order.addedOn)}</p>
                          <p className="mt-2 text-sm text-nv-slate">{order.itemCount} items</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">
                          <p className="font-semibold text-nv-ink">{order.deliveryName || order.email || "Walk-in / unknown"}</p>
                          <p className="mt-1">{order.deliveryPhone || order.buyerPhone || "No phone"}</p>
                          <p className="mt-1 truncate">{order.email || "No email"}</p>
                        </td>
                        <td className="px-5 py-4"><span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(order.status)}`}>{order.status}</span></td>
                        <td className="px-5 py-4 text-sm text-nv-slate">
                          <p className="font-semibold text-nv-ink">{formatCurrency(order.total)}</p>
                          <p className="mt-1">Paid {formatCurrency(order.amountPaid)}</p>
                          <p className={`mt-1 font-semibold ${paymentProgressTone(order)}`}>Balance {formatCurrency(order.balance)}</p>
                          <p className="mt-1 text-xs">{paymentTypeLabel(order.paymentType)}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">
                          <p className="max-w-[240px]">{order.deliveryAddress || "No delivery address"}</p>
                          <p className="mt-1">{order.orderSource || "No source set"}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{order.trackingNumber || "Not assigned"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>No orders matched the current view.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-5 py-4 text-sm text-nv-slate dark:border-white/10">
              <p>Page {pagination.page} of {pagination.totalPages} · {pagination.totalOrders} orders</p>
              <div className="flex items-center gap-2">
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasPreviousPage || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasNextPage || loading} onClick={() => setPage((current) => current + 1)} type="button">Next</button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto pr-1">
          <div className="admin-card rounded-[32px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">
                  {panelMode === "create" ? "Walk-in order" : selectedOrder ? "Order detail" : "Select an order"}
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">
                  {panelMode === "create" ? "Create sales order" : selectedOrder ? `Order #${selectedOrder.id}` : "Sales fulfillment editor"}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {panelMode === "detail" && selectedOrder ? (
                  <button
                    className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10"
                    onClick={() => window.open(`/sales/receipt?order=${selectedOrder.id}`, "_blank", "noopener,noreferrer")}
                    type="button"
                  >
                    Print / download receipt
                  </button>
                ) : null}
                {editorOpen ? (
                  <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                    Close
                  </button>
                ) : null}
              </div>
            </div>

            {!editorOpen ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Select an order to review it, or create a new walk-in order from the sales desk.
              </div>
            ) : panelMode === "create" ? (
              <form className="mt-6 space-y-5" onSubmit={(event) => void handleCreateOrder(event)}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <button className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition ${createOrder.customerMode === "existing" ? "border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300" : "border-black/10 text-nv-slate dark:border-white/10"}`} onClick={() => setCreateOrder((current) => ({ ...current, customerMode: "existing" }))} type="button">Select customer</button>
                  <button className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition ${createOrder.customerMode === "new" ? "border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300" : "border-black/10 text-nv-slate dark:border-white/10"}`} onClick={() => setCreateOrder((current) => ({ ...current, customerMode: "new", customerId: null }))} type="button">Add new customer</button>
                </div>

                {createOrder.customerMode === "existing" ? (
                  <div className="space-y-3">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Find customer</label>
                    <input
                      className="field-control px-4 py-3 text-sm"
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setCustomerQuery(nextValue);
                        if (selectedCustomer && nextValue.trim() !== selectedCustomer.name.trim()) {
                          setSelectedCustomer(null);
                          setCreateOrder((current) => ({
                            ...current,
                            customerId: null
                          }));
                        }
                      }}
                      placeholder="Search by name, email, or phone"
                      value={customerQuery}
                    />
                    <div className="field-soft rounded-[24px] p-4 text-sm text-nv-slate">Selected: {selectedCustomerLabel(selectedCustomer)}</div>
                    {customerLoading ? <div className="text-sm text-nv-slate">Searching customers...</div> : null}
                    {customerResults.length > 0 ? (
                      <div className="space-y-2">
                        {customerResults.map((customer) => (
                          <button className="field-soft block w-full rounded-[20px] p-4 text-left transition hover:bg-black/[0.03] dark:hover:bg-white/[0.03]" key={customer.id} onClick={() => applyCustomer(customer)} type="button">
                            <p className="font-semibold text-nv-ink">{customer.name}</p>
                            <p className="mt-1 text-sm text-nv-slate">{customer.phone || "No phone"} · {customer.email || "No email"}</p>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Customer name</label>
                      <input className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, newCustomerName: event.target.value, deliveryName: current.deliveryName || event.target.value }))} value={createOrder.newCustomerName} />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Phone</label>
                      <input className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, newCustomerPhone: event.target.value, deliveryPhone: current.deliveryPhone || event.target.value }))} value={createOrder.newCustomerPhone} />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Email</label>
                      <input className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, newCustomerEmail: event.target.value }))} value={createOrder.newCustomerEmail} />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Address</label>
                      <input className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, newCustomerAddress: event.target.value, deliveryAddress: current.deliveryAddress || event.target.value }))} value={createOrder.newCustomerAddress} />
                    </div>
                    <div className="md:col-span-2">
                      <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => void handleCreateCustomerInline()} type="button">
                        Create customer now
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Delivery name</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, deliveryName: event.target.value }))} value={createOrder.deliveryName} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Delivery phone</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, deliveryPhone: event.target.value }))} value={createOrder.deliveryPhone} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Delivery address</label>
                    <textarea className="field-control min-h-[100px] px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, deliveryAddress: event.target.value }))} value={createOrder.deliveryAddress} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Delivery note</label>
                    <textarea className="field-control min-h-[80px] px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, deliveryNote: event.target.value }))} value={createOrder.deliveryNote} />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Add products</label>
                  <input className="field-control px-4 py-3 text-sm" onChange={(event) => setProductQuery(event.target.value)} placeholder="Search products by name or SKU" value={productQuery} />
                  {productLoading ? <div className="text-sm text-nv-slate">Searching products...</div> : null}
                  {productResults.length > 0 ? (
                    <div className="space-y-2">
                      {productResults.map((product) => (
                        <button className="field-soft block w-full rounded-[20px] p-4 text-left transition hover:bg-black/[0.03] dark:hover:bg-white/[0.03]" key={product.id} onClick={() => addDraftItem(product)} type="button">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-nv-ink">{product.name}</p>
                              <p className="mt-1 text-sm text-nv-slate">{product.category || "Uncategorized"} · Stock {product.quantity}</p>
                            </div>
                            <span className="shrink-0 font-semibold text-nv-ink">{formatCurrency(product.price)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Draft items</p>
                  <div className="mt-3 space-y-3">
                    {createOrder.items.length > 0 ? (
                      createOrder.items.map((item) => (
                        <div className="field-soft rounded-[24px] p-4" key={item.productId}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <p className="font-semibold text-nv-ink">{item.name}</p>
                              <p className="mt-1 text-sm text-nv-slate">Stock {item.stock} · Unit {formatCurrency(item.price)}</p>
                            </div>
                            <button className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 dark:border-red-900/70 dark:text-red-300" onClick={() => setCreateOrder((current) => ({ ...current, items: current.items.filter((draft) => draft.productId !== item.productId) }))} type="button">Remove</button>
                          </div>
                          <div className="mt-3 grid gap-3 sm:grid-cols-[120px_minmax(0,1fr)]">
                            <input className="field-control px-4 py-3 text-sm" min="1" onChange={(event) => setCreateOrder((current) => ({ ...current, items: current.items.map((draft) => draft.productId === item.productId ? { ...draft, quantity: event.target.value } : draft) }))} step="1" type="number" value={item.quantity} />
                            <div className="field-soft flex items-center rounded-[18px] px-4 py-3 text-sm text-nv-slate">Line total {formatCurrency(item.price * Math.max(Number(item.quantity) || 0, 0))}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">No draft items yet. Search and add products above.</div>
                    )}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Amount paid</label>
                    <input className="field-control px-4 py-3 text-sm" min="0" onChange={(event) => setCreateOrder((current) => ({ ...current, amountPaid: event.target.value }))} step="0.01" type="number" value={createOrder.amountPaid} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Payment type</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, paymentType: event.target.value as PaymentType }))} value={createOrder.paymentType}>
                      <option value="">Select payment type</option>
                      {paymentTypes.map((paymentType) => (
                        <option key={paymentType} value={paymentType}>{paymentTypeLabel(paymentType)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Status</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, status: event.target.value as OrderStatus }))} value={createOrder.status}>
                      {orderStatuses.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Tracking number</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setCreateOrder((current) => ({ ...current, trackingNumber: event.target.value }))} placeholder="Optional" value={createOrder.trackingNumber} />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="field-soft p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Subtotal</p><p className="mt-2 text-xl font-semibold text-nv-ink">{formatCurrency(draftTotals.subtotal)}</p></div>
                  <div className="field-soft p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Amount paid</p><p className="mt-2 text-xl font-semibold text-nv-ink">{formatCurrency(draftTotals.amountPaid)}</p></div>
                  <div className="field-soft p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Balance</p><p className={`mt-2 text-xl font-semibold ${draftTotals.balance <= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-amber-600 dark:text-amber-300"}`}>{formatCurrency(draftTotals.balance)}</p></div>
                </div>

                <div className="flex items-center gap-3">
                  <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={creatingOrder} type="submit">
                    {creatingOrder ? "Creating..." : "Create order"}
                  </button>
                  <button className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">Close</button>
                </div>
              </form>
            ) : loadingEditor ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">Loading order details...</div>
            ) : selectedOrder ? (
              <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Customer</p>
                    <p className="mt-2 font-semibold text-nv-ink">{selectedOrder.deliveryName || selectedOrder.email || "Walk-in / unknown"}</p>
                    <p className="mt-1 text-sm text-nv-slate">{selectedOrder.deliveryPhone || selectedOrder.buyerPhone || "No phone"}</p>
                    <p className="mt-1 text-sm text-nv-slate">{selectedOrder.email || "No email"}</p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Timeline</p>
                    <p className="mt-2 font-semibold text-nv-ink">{formatDateTime(selectedOrder.addedOn)}</p>
                    <p className="mt-1 text-sm text-nv-slate">Source: {selectedOrder.orderSource || "Not recorded"}</p>
                    <p className="mt-1 text-sm text-nv-slate">Items: {selectedOrder.itemCount}</p>
                  </div>
                </div>

                <div className="field-soft p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Delivery</p>
                  <p className="mt-2 text-sm text-nv-ink">{selectedOrder.deliveryAddress || "No delivery address"}</p>
                  {selectedOrder.deliveryNote ? <p className="mt-2 text-sm text-nv-slate">Note: {selectedOrder.deliveryNote}</p> : null}
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Status</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, status: event.target.value as OrderStatus }))} value={editor.status}>
                      {orderStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Tracking number</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, trackingNumber: event.target.value }))} placeholder="e.g. NVT-240318-001" value={editor.trackingNumber} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Amount paid</label>
                    <input className="field-control px-4 py-3 text-sm" min="0" onChange={(event) => setEditor((current) => ({ ...current, amountPaid: event.target.value }))} step="0.01" type="number" value={editor.amountPaid} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Payment type</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, paymentType: event.target.value as PaymentType }))} value={editor.paymentType}>
                      <option value="">Select payment type</option>
                      {paymentTypes.map((paymentType) => <option key={paymentType} value={paymentType}>{paymentTypeLabel(paymentType)}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="field-soft p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Order total</p><p className="mt-2 text-xl font-semibold text-nv-ink">{formatCurrency(selectedOrder.total)}</p></div>
                  <div className="field-soft p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Discount</p><p className="mt-2 text-xl font-semibold text-nv-ink">{formatCurrency(selectedOrder.discount)}</p></div>
                  <div className="field-soft p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Balance</p><p className={`mt-2 text-xl font-semibold ${paymentProgressTone(selectedOrder)}`}>{formatCurrency(selectedOrder.balance)}</p></div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Line items</p>
                  <div className="mt-3 space-y-3">
                    {selectedOrder.items.length > 0 ? selectedOrder.items.map((item) => (
                      <div className="field-soft flex items-start justify-between gap-4 p-4" key={item.id}>
                        <div className="min-w-0">
                          {item.productId && item.slug ? <Link className="font-semibold text-nv-ink transition hover:text-nv-electric" to={`/shop/product/${item.slug}/${item.productId}`}>{item.name}</Link> : <p className="font-semibold text-nv-ink">{item.name}</p>}
                          <p className="mt-1 text-sm text-nv-slate">Qty {item.quantity} x {formatCurrency(item.unitPrice)}</p>
                        </div>
                        <p className="shrink-0 font-semibold text-nv-ink">{formatCurrency(item.total)}</p>
                      </div>
                    )) : <div className="rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">No order items were found for this sale.</div>}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={saving} type="submit">{saving ? "Saving..." : "Save changes"}</button>
                  <button className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">Close</button>
                </div>
              </form>
            ) : (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">Order details are unavailable.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
